/**
 * Actions Module - Kanban Board
 * Main page for managing action items
 */
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { KanbanBoard } from '@/components/actions/KanbanBoard'
import { FilterBar } from '@/components/actions/FilterBar'
import { CreateActionItemModal } from '@/components/actions/CreateActionItemModal'
import { ActionItemDetailModal } from '@/components/actions/ActionItemDetailModal'
import { useActionItems } from '@/hooks/useActions'
import type { ActionFilters } from '@/types/actions'

export function Actions() {
  const [filters, setFilters] = useState<ActionFilters>({})
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [createModalStatus, setCreateModalStatus] = useState<string>('backlog')
  
  const { data: actionItems, isLoading, error } = useActionItems(filters)
  
  const handleCreateClick = (status: string) => {
    setCreateModalStatus(status)
    setCreateModalOpen(true)
  }
  
  const handleCardClick = (actionId: string) => {
    setSelectedActionId(actionId)
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-destructive">Error loading actions</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="h-full flex flex-col">
      {/* Filter Bar */}
      <FilterBar filters={filters} onFiltersChange={setFilters} />
      
      {/* Kanban Board */}
      <div className="flex-1 overflow-auto">
        <KanbanBoard
          actionItems={actionItems || { backlog: [], doing: [], review: [], done: [] }}
          onCardClick={handleCardClick}
          onCreateClick={handleCreateClick}
        />
      </div>
      
      {/* Create Action Item Modal */}
      <CreateActionItemModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        defaultStatus={createModalStatus}
      />
      
      {/* Action Item Detail Modal */}
      <ActionItemDetailModal
        actionId={selectedActionId}
        open={!!selectedActionId}
        onOpenChange={(open) => !open && setSelectedActionId(null)}
      />
    </div>
  )
}
